/* ══════════════════════════════════════════════════════════════════
   Die frisch gerechneten Zuschläge eines Postens — und was ein Mensch
   daran entschieden hat.

   Eine eigene Datei ohne Abhängigkeiten, damit die Regel ohne Browser
   prüfbar ist (`test/zuschlagmenge.test.ts`). `daten/merkliste.ts` reicht
   beide Funktionen unter ihrem alten Namen weiter.
   ══════════════════════════════════════════════════════════════════ */

import type { Merkzuschlag } from "./merkliste";

/**
 * **Die frisch gerechneten Zuschläge — und was ein Mensch daran geändert hat.**
 *
 * Gerechnet wird neu (Regel 4): Ein anderer Zeitraum ist ein anderer Betrag.
 * Was ein Mensch entschieden hat, überlebt das aber — wer die Anlieferung
 * abgewählt oder einen Satz zugesagt hat, hat das nicht für genau diesen
 * Zeitraum getan. Verglichen wird über den Code; ein Zuschlag, den es heute
 * nicht mehr gibt, fällt weg.
 *
 * **Und der Schalter „Kunde holt ab" zählt mit** (Migration 0127): Was er
 * abgewählt hat, bleibt abgewählt, solange er an ist. Bis zum 18.09.2026
 * stand diese Zusammenführung dreimal im Repo — in `merken`, in der
 * Sammelkarte und im Abschluss —, und zwei davon warfen `aus` weg: Ein
 * geänderter Zeitraum brachte die Anlieferung zurück, ohne dass jemand den
 * Schalter angefasst hätte.
 *
 * **Die Menge ist keine Entscheidung, sie folgt dem Zeitraum** (08.10.2026).
 * Bis dahin blieb auch `menge` von vorher stehen. Niemand setzt sie von Hand;
 * sie kommt aus der Preisfindung wie das Netto. Ein Posten über fünf Tage,
 * auf drei verkürzt, zeigte so „5 Tage · 10,50 = 31,50" — und mit einem
 * zugesagten Satz ging er mit fünf Tagen in den Vorgang (5 × 12,00 statt
 * 3 × 12,00). Dieselbe Regel wie im Postenblatt: neu die Zahlen, alt nur
 * `aus`, `ausWegenAbholung` und `einzelpreis`.
 */
export function zuschlaegeZusammenfuehren(
  alt: Merkzuschlag[],
  neu: Merkzuschlag[],
): Merkzuschlag[] {
  return neu.map((z) => {
    const vorher = alt.find((x) => x.code === z.code);
    if (!vorher) return z;
    return {
      ...z,
      ...(vorher.aus ? { aus: true as const } : {}),
      ...(vorher.ausWegenAbholung ? { ausWegenAbholung: true as const } : {}),
      ...(vorher.einzelpreis === undefined ? {} : { einzelpreis: vorher.einzelpreis }),
    };
  });
}

/** Was ein Zuschlag an diesem Posten wirklich kostet — je Stück. */
export function zuschlagNetto(z: Merkzuschlag): number {
  if (z.aus) return 0;
  if (z.einzelpreis === undefined) return z.netto;
  return Math.round(z.einzelpreis * (z.menge ?? 1) * 100) / 100;
}
